import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { User, ShoppingBag, Settings, Star, MapPin, Phone, Mail, Edit2, Plus, Check, AlertCircle, Lock, Trash2, Send, CreditCard, Clock, Wallet, Download, Share2, RefreshCw, Camera, X, MessageSquare, IndianRupee } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs, orderBy, onSnapshot } from 'firebase/firestore';
import { db, storage } from '../config/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { sendEmailVerification, updatePassword, updateProfile, EmailAuthProvider, reauthenticateWithCredential, reload } from 'firebase/auth';
import type { UserProfile, UserAddress } from '../types/user';
import LoadingSpinner from '../components/LoadingSpinner';
import { generateInvoicePDF, shareOrderDetails } from '../utils/invoiceGenerator';
import { useCart } from '../contexts/CartContext';
import PlatformFeedbackModal from '../components/PlatformFeedbackModal';
import { calculateAndUpdateAverageRating } from '../services/userService';
import PhonePeService from '../utils/phonepeService';

export default function AccountPage() {
  const { user, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [activeTab, setActiveTab] = useState<'profile' | 'addresses' | 'orders' | 'reviews' | 'settings'>('profile');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [editingAddress, setEditingAddress] = useState<UserAddress | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    fullName: '',
    phoneNumber: '',
    email: '',
  });
  const [newAddress, setNewAddress] = useState<Partial<UserAddress>>({
    fullName: '',
    phone: '',
    houseNumber: '',
    streetAddress: '',
    landmark: '',
    city: '',
    state: '',
    country: 'India',
    pincode: '',
    isDefault: false
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [orders, setOrders] = useState<any[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showProfileWarning, setShowProfileWarning] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackOrder, setFeedbackOrder] = useState<any>(null);
  const [reviews, setReviews] = useState<any[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  const fetchUserProfile = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    if (!db) {
      console.error('Firestore is not initialized');
      setError('Database connection error');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Calculate rating silently in background (don't wait)
      calculateAndUpdateAverageRating(user.uid).catch(() => {
        // Silently ignore rating calculation errors
      });
      
      // Get user document reference
      const userRef = doc(db, 'users', user.uid);
      
      // Fetch with timeout - max 5 seconds
      const timeoutPromise = new Promise<never>((_, reject) => 
        setTimeout(() => reject(new Error('timeout')), 5000)
      );
      
      const userDoc = await Promise.race([
        getDoc(userRef),
        timeoutPromise
      ]).catch(err => {
        // If offline or timeout, use basic auth data
        return null;
      });
      
      if (!userDoc || !userDoc.exists()) {
        // Initialize user profile if it doesn't exist
        const newProfile: UserProfile = {
          uid: user.uid,
          email: user.email || '',
          fullName: user.displayName || '',
          displayName: user.displayName || '',
          phoneNumber: user.phoneNumber || '',
          isPhoneVerified: false,
          isEmailVerified: user.emailVerified,
          profileImage: user.photoURL || '',
          addresses: [],
          defaultAddressId: undefined,
          role: 'user',
          totalOrders: 0,
          totalSpent: 0,
          averageRating: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        try {
          await setDoc(userRef, newProfile);
          setUserProfile(newProfile);
          setEditForm({
            fullName: newProfile.fullName,
            phoneNumber: newProfile.phoneNumber || '',
            email: newProfile.email
          });
        } catch (err) {
          console.error('Error creating new user profile:', err);
          throw new Error('Failed to create user profile');
        }
      } else {
        const profileData = userDoc.data() as UserProfile;
        
        // Validate required fields
        if (!profileData.uid || !profileData.email || !profileData.fullName) {
          console.error('Invalid profile data:', profileData);
          throw new Error('Invalid user profile data');
        }

        // Sync emailVerified status from Firebase Auth to Firestore
        if (profileData.isEmailVerified !== user.emailVerified) {
          profileData.isEmailVerified = user.emailVerified;
          
          // Update Firestore with the correct email verification status
          try {
            await setDoc(userRef, { 
              isEmailVerified: user.emailVerified,
              updatedAt: new Date().toISOString()
            }, { merge: true });
          } catch (syncErr) {
            console.error('Error syncing email verification status:', syncErr);
            // Don't throw - we can still use the corrected data from auth
          }
        }

        // Find and set the default/selected address
        if (profileData.addresses && profileData.addresses.length > 0) {
          const defaultAddress = profileData.addresses.find(
            addr => addr.id === profileData.defaultAddressId
          ) || profileData.addresses[0];

          if (!profileData.defaultAddressId) {
            profileData.defaultAddressId = defaultAddress.id;
          }
        }
        
        setUserProfile(profileData);
        setEditForm({
          fullName: profileData.fullName,
          phoneNumber: profileData.phoneNumber || '',
          email: profileData.email
        });
      }
    } catch (err) {
      console.error('Error fetching user profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to load user profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    if (!user) {
      console.log('No user found, cannot fetch orders');
      return;
    }

    try {
      setOrdersLoading(true);
      setError(null);
      
      // Check if database is initialized
      if (!db) {
        throw new Error('Database not initialized');
      }

      // Query orders collection where userId matches current user
      const ordersRef = collection(db, 'orders');
      
      // Fetch orders with timeout
      const ordersTimeoutPromise = new Promise<never>((_, reject) => 
        setTimeout(() => reject(new Error('Orders fetch timeout')), 5000)
      );

      try {
        const q = query(
          ordersRef,
          where('userId', '==', user.uid),
          orderBy('createdAt', 'desc')
        );
        
        const querySnapshot = await Promise.race([
          getDocs(q),
          ordersTimeoutPromise
        ]);
        
        const fetchedOrders = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setOrders(fetchedOrders);
      } catch (indexError: any) {
        // If timeout or offline, show empty orders
        if (indexError.message === 'Orders fetch timeout' || indexError.code === 'unavailable') {
          setOrders([]);
          return;
        }
        
        // If index error, try without orderBy
        const q = query(
          ordersRef,
          where('userId', '==', user.uid)
        );
        
        const querySnapshot = await Promise.race([
          getDocs(q),
          ordersTimeoutPromise
        ]).catch(() => {
          // If still fails, return empty
          setOrders([]);
          throw new Error('skip');
        });
        
        const fetchedOrders = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Sort in memory
        fetchedOrders.sort((a: any, b: any) => {
          const dateA = new Date(a.createdAt || 0).getTime();
          const dateB = new Date(b.createdAt || 0).getTime();
          return dateB - dateA;
        });
        
        setOrders(fetchedOrders);
      }
    } catch (err: any) {
      // Silently handle - just show empty orders
      if (err.message !== 'skip') {
        setOrders([]);
      }
      
      let errorMessage = 'Failed to load orders';
      if (err.code === 'permission-denied') {
        errorMessage = 'Permission denied. Please check Firestore security rules.';
      } else if (err.message) {
        errorMessage = `Failed to load orders: ${err.message}`;
      }
      
      setError(errorMessage);
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  };

  const fetchCustomerReviews = async () => {
    if (!user) {
      setReviews([]);
      return;
    }

    try {
      setReviewsLoading(true);
      
      // Fetch reviews from customerFeedbacks collection where customerId matches
      const reviewsRef = collection(db, 'customerFeedbacks');
      const q = query(
        reviewsRef,
        where('customerId', '==', user.uid),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q).catch(async (err) => {
        // If orderBy fails, try without it
        if (err.code === 'failed-precondition' || err.message?.includes('index')) {
          const qWithoutOrder = query(
            reviewsRef,
            where('customerId', '==', user.uid)
          );
          return getDocs(qWithoutOrder);
        }
        throw err;
      });
      
      // Extract all unique provider and order IDs
      const providerIds = new Set<string>();
      const orderIds = new Set<string>();
      
      querySnapshot.docs.forEach(docSnapshot => {
        const reviewData = docSnapshot.data();
        if (reviewData.providerId) providerIds.add(reviewData.providerId);
        if (reviewData.orderId) orderIds.add(reviewData.orderId);
      });
      
      // Batch fetch providers and orders in parallel (1 query each instead of N)
      const [providersSnapshot, ordersSnapshot] = await Promise.all([
        providerIds.size > 0 
          ? getDocs(query(collection(db, 'serviceProviders'), where('__name__', 'in', Array.from(providerIds).slice(0, 10))))
          : Promise.resolve(null),
        orderIds.size > 0
          ? getDocs(query(collection(db, 'orders'), where('__name__', 'in', Array.from(orderIds).slice(0, 10))))
          : Promise.resolve(null)
      ]);
      
      // Create lookup maps
      const providersMap = new Map();
      const ordersMap = new Map();
      
      if (providersSnapshot) {
        providersSnapshot.docs.forEach(doc => {
          const data = doc.data();
          providersMap.set(doc.id, {
            id: doc.id,
            name: data.name || data.fullName || 'Service Provider',
            profileImage: data.profileImage || data.profilePicture || null,
            rating: data.rating || data.averageRating || 0,
            phone: data.phone || null
          });
        });
      }
      
      if (ordersSnapshot) {
        ordersSnapshot.docs.forEach(doc => {
          const data = doc.data();
          ordersMap.set(doc.id, {
            id: doc.id,
            serviceName: data.serviceName || data.service?.name || 'Service',
            totalAmount: data.totalAmount || 0,
            createdAt: data.createdAt,
            completedAt: data.completedAt
          });
        });
      }
      
      // Map reviews with fetched data
      const fetchedReviews = querySnapshot.docs.map(docSnapshot => {
        const reviewData = docSnapshot.data();
        return {
          id: docSnapshot.id,
          ...reviewData,
          provider: reviewData.providerId ? (providersMap.get(reviewData.providerId) || null) : null,
          order: reviewData.orderId ? (ordersMap.get(reviewData.orderId) || null) : null
        };
      });
      
      // Sort by date if orderBy didn't work
      fetchedReviews.sort((a: any, b: any) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
      });
      
      setReviews(fetchedReviews);
    } catch (err: any) {
      console.error('Error fetching reviews:', err);
      setReviews([]);
    } finally {
      setReviewsLoading(false);
    }
  };

  useEffect(() => {
    const initializeProfile = async () => {
      // Wait for auth to be ready
      if (authLoading) {
        return;
      }
      
      // If no user, reset profile and stop loading
      if (!user?.uid) {
        setUserProfile(null);
        setLoading(false);
        return;
      }
      
      fetchUserProfile();
    };

    initializeProfile();
  }, [user?.uid, authLoading]); // Include authLoading in dependencies

  // Check for email verification status updates
  useEffect(() => {
    // ONLY check if email is NOT verified - stop checking once verified!
    if (!user || user.emailVerified || !userProfile || userProfile.isEmailVerified) {
      return; // Skip if already verified
    }

    let isChecking = false; // Prevent multiple simultaneous checks

    const checkEmailVerification = async () => {
      if (isChecking) return; // Prevent concurrent checks
      isChecking = true;

      try {
        // Reload the user to get the latest emailVerified status from Firebase Auth
        await reload(user);
        
        // If email was just verified, refresh the profile to show the updated status
        if (user.emailVerified && !userProfile.isEmailVerified) {
          console.log('Email verification detected - refreshing profile');
          await fetchUserProfile();
        }
      } catch (error) {
        console.error('Error checking email verification:', error);
      } finally {
        isChecking = false;
      }
    };

    // Only check when the window regains focus (user comes back from verifying email)
    const handleFocus = () => {
      checkEmailVerification();
    };

    window.addEventListener('focus', handleFocus);
    
    // Set up a LONGER interval to check periodically (every 30 seconds instead of 5)
    // This dramatically reduces Firebase API calls
    const intervalId = setInterval(checkEmailVerification, 30000);

    return () => {
      window.removeEventListener('focus', handleFocus);
      clearInterval(intervalId);
    };
  }, [user, userProfile?.isEmailVerified]); // Only depend on verification status

  // Fetch orders on mount and when user changes
  useEffect(() => {
    if (user) {
      fetchOrders();
    }
  }, [user]);
  
  // Real-time listener for order status changes
  useEffect(() => {
    if (!user) return;
    
    const ordersQuery = query(
      collection(db, 'orders'),
      where('userId', '==', user.uid)
    );
    
    console.log('Setting up real-time listener for orders...');
    
    const unsubscribe = onSnapshot(ordersQuery, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'modified') {
          const updatedOrder = { id: change.doc.id, ...change.doc.data() } as any;
          console.log('📢 Order status changed:', updatedOrder.id, 'New status:', updatedOrder.status);
          
          // Check if status changed to partial-completed
          if (updatedOrder.status === 'partial-completed') {
            // Show notification
            setSuccessMessage('⚠️ Service Partially Completed - Payment Required!');
            setTimeout(() => setSuccessMessage(null), 5000);
            
            // Scroll to the order
            setTimeout(() => {
              const orderElement = document.getElementById(`order-${updatedOrder.id}`);
              if (orderElement) {
                orderElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 500);
          }
          
          // Reload orders to reflect changes
          console.log('Reloading orders due to status change...');
          fetchOrders();
        }
      });
    }, (error) => {
      console.error('Error in real-time listener:', error);
    });
    
    return () => {
      console.log('Cleaning up real-time listener');
      unsubscribe();
    };
  }, [user]);

  // Fetch reviews when reviews tab is active
  useEffect(() => {
    if (user && activeTab === 'reviews') {
      fetchCustomerReviews();
    }
  }, [user, activeTab]);

  // Handle tab parameter from URL
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && ['profile', 'addresses', 'orders', 'reviews', 'settings'].includes(tab)) {
      setActiveTab(tab as 'profile' | 'addresses' | 'orders' | 'reviews' | 'settings');
    }
  }, [searchParams]);

  // Calculate cancellation policy display (wrapper for calculateCancellationFee)
  const calculateCancellationPolicy = (order: any) => {
    const cancellationData = calculateCancellationFee(order) || {};
    const hoursUntilService = typeof cancellationData.hoursUntilService === 'number' ? cancellationData.hoursUntilService : -1;

    let message = '';
    if (hoursUntilService >= 48) {
      message = 'Free Cancellation - Full Refund';
    } else if (hoursUntilService >= 24) {
      message = '50% Refund Available';
    } else if (hoursUntilService >= 6) {
      message = '25% Refund Available';
    } else {
      message = 'No Refund Available';
    }

    return {
      message,
      refundPercentage: cancellationData.refundPercentage || 0,
      cancellationFee: cancellationData.fee || 0,
      refundAmount: cancellationData.refundAmount || 0,
      hoursUntilService
    };
  };

  // Handle cancel booking click
  const handleCancelClick = (order: any) => {
    setSelectedOrder(order);
    setShowCancelModal(true);
    setCancelReason('');
  };

  // Calculate cancellation fee based on service date
  const calculateCancellationFee = (order: any) => {
    if (!order.items || order.items.length === 0) {
      return { fee: 0, refundAmount: 0, refundPercentage: 100 };
    }

    // Get the earliest service date
    const serviceDates = order.items
      .filter((item: any) => item.scheduling?.date)
      .map((item: any) => new Date(item.scheduling.date));
    
    if (serviceDates.length === 0) {
      return { fee: 0, refundAmount: 0, refundPercentage: 100 };
    }

    const earliestServiceDate = new Date(Math.min(...serviceDates.map((d: Date) => d.getTime())));
    const now = new Date();
    const hoursUntilService = (earliestServiceDate.getTime() - now.getTime()) / (1000 * 60 * 60);

    // Calculate order total
    const orderTotal = order.items.reduce((sum: number, item: any) => 
      sum + (item.payment?.total || item.price * item.quantity), 0);

    let refundPercentage = 100;
    let cancellationFeePercentage = 0;

    // Cancellation policy
    if (hoursUntilService >= 48) {
      // 48+ hours: Free cancellation - 100% refund
      refundPercentage = 100;
      cancellationFeePercentage = 0;
    } else if (hoursUntilService >= 24) {
      // 24-48 hours: 50% cancellation fee - 50% refund
      refundPercentage = 50;
      cancellationFeePercentage = 50;
    } else if (hoursUntilService >= 6) {
      // 6-24 hours: 75% cancellation fee - 25% refund
      refundPercentage = 25;
      cancellationFeePercentage = 75;
    } else {
      // Less than 6 hours: 100% cancellation fee - No refund
      refundPercentage = 0;
      cancellationFeePercentage = 100;
    }

    const cancellationFee = Math.round(orderTotal * (cancellationFeePercentage / 100));
    const refundAmount = Math.round(orderTotal - cancellationFee);

    return { 
      fee: cancellationFee, 
      refundAmount, 
      refundPercentage,
      orderTotal,
      hoursUntilService: Math.round(hoursUntilService)
    };
  };

  // Confirm cancellation with payment if required
  const handleConfirmCancellation = async () => {
    if (!selectedOrder || !user) {
      return;
    }

    try {
      setCancelLoading(true);
      setError(null);

      // Calculate cancellation fee
      const cancellationData = calculateCancellationFee(selectedOrder);
      console.log('Cancellation data:', cancellationData);

      // If cancellation fee exists, initiate payment first
      if (cancellationData.fee > 0) {
        console.log(`Cancellation fee: ₹${cancellationData.fee}, initiating PhonePe payment...`);
        
        try {
          // Initiate PhonePe payment for cancellation fee
          const paymentResponse = await PhonePeService.initiatePayment({
            orderId: `CANCEL-${selectedOrder.id}`, // Unique cancellation payment ID
            amount: cancellationData.fee,
            customerName: selectedOrder.address?.fullName || user?.displayName || 'Customer',
            customerEmail: selectedOrder.address?.email || user?.email || '',
            customerPhone: selectedOrder.address?.phone || user?.phoneNumber || '',
            userId: user.uid,
            orderData: {
              type: 'cancellation_fee',
              originalOrderId: selectedOrder.id,
              cancellationReason: cancelReason || 'No reason provided',
              refundAmount: cancellationData.refundAmount,
              cancellationFee: cancellationData.fee
            }
          });

          if (paymentResponse.success) {
            const redirectUrl = PhonePeService.getRedirectUrl(paymentResponse);
            
            if (redirectUrl) {
              // Store cancellation details in localStorage for post-payment processing
              localStorage.setItem('pendingCancellation', JSON.stringify({
                orderId: selectedOrder.id,
                cancelReason: cancelReason || 'No reason provided',
                cancellationData,
                timestamp: new Date().toISOString()
              }));
              
              console.log('✅ Redirecting to PhonePe for cancellation fee payment...');
              window.location.href = redirectUrl;
              return; // Exit function as we're redirecting
            } else {
              throw new Error('Payment redirect URL not available');
            }
          } else {
            throw new Error(paymentResponse.message || 'Payment initiation failed');
          }
        } catch (paymentError: any) {
          console.error('❌ Payment initiation failed:', paymentError);
          setError(`Order cannot be cancelled: ${paymentError.message || 'Payment failed'}`);
          setCancelLoading(false);
          return; // Don't cancel order if payment fails
        }
      } else {
        // No cancellation fee - proceed with direct cancellation
        console.log('No cancellation fee, proceeding with free cancellation');
        
        const orderRef = doc(db, 'orders', selectedOrder.id);
        
        await updateDoc(orderRef, {
          cancelledAt: new Date().toISOString(),
          cancelReason: cancelReason || 'No reason provided',
          cancelledBy: 'user',
          cancellationFee: 0,
          refundAmount: cancellationData.refundAmount,
          refundStatus: 'approved',
          updatedAt: new Date().toISOString(),
          items: selectedOrder.items?.map((item: any) => ({
            ...item,
            status: 'cancelled',
            cancelledAt: new Date().toISOString(),
            cancelReason: cancelReason || 'Cancelled by user'
          }))
        });

        console.log('✅ Order cancelled successfully (free cancellation)');
        
        await fetchOrders();
        
        setShowCancelModal(false);
        setSelectedOrder(null);
        setCancelReason('');
        setSuccessMessage(`Booking cancelled successfully. Refund: ₹${cancellationData.refundAmount}`);
      }
      
    } catch (err) {
      console.error('Error cancelling order:', err);
      setError('Failed to cancel booking. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  // Handle Rebook - Add items back to cart and navigate to cart
  const handleRebook = async (order: any) => {
    try {
      if (!order.items || order.items.length === 0) {
        setError('No items found in this order');
        return;
      }

      // Add each item back to cart
      order.items.forEach((item: any) => {
        const cartItem = {
          id: item.id || item.serviceId || `service-${Date.now()}`,
          name: item.name,
          price: item.price,
          originalPrice: item.originalPrice,
          image: item.image,
          quantity: item.quantity,
          date: order.scheduling?.date || '',
          time: order.scheduling?.time || '',
          duration: item.duration || '60 min',
          provider: item.provider || 'RAPSAPP Service',
          addons: item.addons || []
        };
        addToCart(cartItem);
      });

      setSuccessMessage('Services added to cart! Redirecting...');
      setTimeout(() => {
        navigate('/cart');
      }, 1500);
    } catch (err) {
      console.error('Error rebooking:', err);
      setError('Failed to rebook services. Please try again.');
    }
  };

  // Calculate unpaid amount for an order
  const calculateUnpaidAmount = (order: any) => {
    // Use unified amountDue if available
    if (order.amountDue !== undefined && order.amountDue !== null) {
      return Math.max(0, order.amountDue);
    }
    
    // Fallback calculation for backwards compatibility
    const paidAmount = order.amountPaid || order.paidAmount || 0;
    
    // Use unified total fields
    const baseTotal = order.total || order.payment?.total || 0;
    const additionalTotal = order.additionalTotal || 0;
    
    // Calculate total from items if not available
    let itemsTotal = baseTotal;
    if (itemsTotal === 0 && order.items) {
      itemsTotal = order.items.reduce((sum: number, item: any) => 
        sum + (item.payment?.total || item.price * item.quantity), 0) || 0;
    }
    
    // Legacy additional charges
    const additionalCharges = order.additionalCharges || [];
    const legacyAdditionalTotal = additionalCharges.reduce((sum: number, charge: any) => sum + (charge.amount || 0), 0);
    
    const grandTotal = order.grandTotal || (itemsTotal + additionalTotal + legacyAdditionalTotal);
    
    return Math.max(0, grandTotal - paidAmount);
  };
  
  // Check if order is fully paid
  const isOrderPaid = (order: any) => {
    // Check unified amountDue
    if (order.amountDue !== undefined && order.amountDue !== null) {
      return order.amountDue <= 0;
    }
    
    // Check payment status
    if (order.payment?.status === 'completed' && (order.additionalTotal || 0) === 0) {
      return true;
    }
    
    // Calculate
    return calculateUnpaidAmount(order) <= 0;
  };
  
  // Get payment method display
  const getPaymentMethodDisplay = (order: any) => {
    const method = order.payment?.method || order.items?.[0]?.payment?.method || '';
    if (method.toLowerCase() === 'phonepe') return '💳 PhonePe';
    if (method.toLowerCase() === 'pas') return '💵 Pay After Service';
    if (method === 'card') return '💳 Card';
    if (method === 'upi') return '📱 UPI';
    return method || 'Pay After Service';
  };

  // Handle Pay Now for unpaid orders
  const handlePayNow = async (order: any) => {
    console.log('🔔 Pay Now clicked');
    console.log('📦 Order details:', order);
    console.log('👤 User:', user);
    
    if (!user) {
      setError('Please login to make payment');
      setTimeout(() => setError(null), 5000);
      return;
    }

    try {
      const unpaidAmount = calculateUnpaidAmount(order);
      console.log('💰 Unpaid amount:', unpaidAmount);
      
      if (unpaidAmount <= 0) {
        setError('No pending payment for this order');
        setTimeout(() => setError(null), 5000);
        return;
      }

      console.log('🚀 Initiating PhonePe payment...');
      console.log('Payment details:', {
        orderId: order.id,
        amount: unpaidAmount,
        customerName: order.address?.fullName || user?.displayName || 'Customer',
        customerEmail: order.address?.email || user?.email || '',
        customerPhone: order.address?.phone || user?.phoneNumber || '',
        userId: user.uid
      });

      // Initiate PhonePe payment with order reference
      const paymentResponse = await PhonePeService.initiatePayment({
        orderId: order.id, // Use order ID for cross-referencing
        amount: unpaidAmount,
        customerName: order.address?.fullName || user?.displayName || 'Customer',
        customerEmail: order.address?.email || user?.email || '',
        customerPhone: order.address?.phone || user?.phoneNumber || '',
        userId: user.uid, // Customer ID for cross-referencing
      });

      console.log('📡 Payment response:', paymentResponse);

      if (paymentResponse.success) {
        const redirectUrl = PhonePeService.getRedirectUrl(paymentResponse);
        console.log('🔗 Redirect URL:', redirectUrl);
        
        if (redirectUrl) {
          console.log('✅ Redirecting to PhonePe...');
          window.location.href = redirectUrl;
        } else {
          console.error('❌ No redirect URL in response');
          setError('Failed to get payment URL');
          setTimeout(() => setError(null), 5000);
        }
      } else {
        console.error('❌ Payment initiation failed:', paymentResponse.message);
        setError(paymentResponse.message || 'Failed to initiate payment');
        setTimeout(() => setError(null), 5000);
      }
    } catch (err: any) {
      console.error('❌ Payment error:', err);
      console.error('Error stack:', err.stack);
      setError(`Payment failed: ${err.message || 'Unknown error'}`);
      setTimeout(() => setError(null), 5000);
    }
  };

  // Handle Download Invoice
  const handleDownloadInvoice = (order: any) => {
    console.log('📥 Download Invoice clicked for order:', order.id);
    try {
      const additionalCharges = order.additionalCharges || [];
      
      // Calculate total from items
      const itemsTotal = order.items?.reduce((sum: number, item: any) => 
        sum + (item.payment?.total || item.price * item.quantity), 0) || 0;
      
      const additionalTotal = additionalCharges.reduce((sum: number, charge: any) => sum + (charge.amount || 0), 0);
      const finalTotal = order.finalAmount || (itemsTotal + additionalTotal);
      
      // Get timeline dates
      const timeline: any = {
        placed: order.createdAt
      };
      
      if (order.acceptedAt || order.confirmedAt) {
        const date = order.acceptedAt || order.confirmedAt;
        timeline.confirmed = date?.toDate ? date.toDate() : date;
      }
      
      if (order.startedAt || order.serviceStartTime) {
        const date = order.startedAt || order.serviceStartTime;
        timeline.started = date?.toDate ? date.toDate() : date;
      }
      
      if (order.completedAt || order.serviceCompletedTime) {
        const date = order.completedAt || order.serviceCompletedTime;
        timeline.completed = date?.toDate ? date.toDate() : date;
      }
      
      const invoiceData = {
        orderId: order.id,
        orderDate: order.createdAt,
        customerName: order.address?.fullName || user?.displayName || 'Customer',
        customerEmail: order.address?.email || user?.email || '',
        customerPhone: order.address?.phone || '',
        address: order.address,
        items: order.items || [],
        additionalCharges: additionalCharges,
        payment: {
          subtotal: itemsTotal,
          tax: 0,
          total: finalTotal,
          method: order.items?.[0]?.payment?.method || 'N/A',
          status: order.items?.every((item: any) => item.payment?.status === 'completed') ? 'completed' : 'pending',
          paidAmount: order.paidAmount || finalTotal
        },
        timeline: timeline,
        status: order.status,
        providerName: order.providerName,
        providerPhone: order.providerPhone
      };

      console.log('📄 Calling generateInvoicePDF...');
      generateInvoicePDF(invoiceData);
      setSuccessMessage('Invoice downloaded successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('❌ Error generating invoice:', err);
      setError(`Failed to generate invoice: ${err.message || 'Unknown error'}`);
      setTimeout(() => setError(null), 5000);
    }
  };

  // Handle Share Order
  const handleShareOrder = async (order: any) => {
    console.log('📤 Share button clicked for order:', order.id);
    try {
      const result = await shareOrderDetails(order);
      console.log('📤 Share result:', result);
      
      if (result.success) {
        if (result.copied) {
          setSuccessMessage('Order details copied to clipboard!');
        } else {
          setSuccessMessage('Order details shared successfully!');
        }
        setTimeout(() => setSuccessMessage(null), 3000);
      } else if (!result.cancelled) {
        setError('Failed to share order details');
        setTimeout(() => setError(null), 5000);
      }
    } catch (err: any) {
      console.error('❌ Error sharing order:', err);
      setError(`Failed to share order: ${err.message || 'Unknown error'}`);
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleUpdateProfile = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        fullName: editForm.fullName,
        phoneNumber: editForm.phoneNumber,
        displayName: editForm.fullName,
        updatedAt: new Date().toISOString()
      });
      
      // Update Firebase Auth profile
      await updateProfile(user, {
        displayName: editForm.fullName
      });
      
      // Force reload user to sync auth state
      await reload(user);
      
      await fetchUserProfile();
      setIsEditing(false);
      setSuccessMessage('Profile updated successfully');
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  // Handle profile photo upload
  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size should be less than 5MB');
      return;
    }

    try {
      setUploadingPhoto(true);
      setError(null);

      // Create a reference to the file location
      const storageRef = ref(storage, `users/${user.uid}/profile/profile.jpg`);

      // Upload the file
      await uploadBytes(storageRef, file);

      // Get the download URL
      const photoURL = await getDownloadURL(storageRef);

      // Update user profile in Firestore
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        profileImage: photoURL,
        updatedAt: new Date().toISOString()
      });

      // Update Firebase Auth profile
      await updateProfile(user, {
        photoURL: photoURL
      });

      // Force reload user to sync auth state
      await reload(user);

      // Refresh profile
      await fetchUserProfile();
      setSuccessMessage('Profile photo updated successfully');
    } catch (err: any) {
      console.error('Error uploading photo:', err);
      setError(`Failed to upload photo: ${err.message || 'Unknown error'}`);
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Check if profile is complete
  const isProfileComplete = (profile: UserProfile | null): boolean => {
    if (!profile) return false;
    
    const hasBasicInfo = !!(
      profile.fullName && 
      profile.phoneNumber && 
      profile.email
    );
    
    const hasAddress = profile.addresses && profile.addresses.length > 0;
    
    return hasBasicInfo && hasAddress;
  };

  // Check profile completeness on load
  useEffect(() => {
    if (userProfile && activeTab === 'profile') {
      const isComplete = isProfileComplete(userProfile);
      setShowProfileWarning(!isComplete);
    }
  }, [userProfile, activeTab]);

  const handleAddAddress = async () => {
    if (!user || !userProfile) return;
    
    // If we're editing, use the update function instead
    if (editingAddress) {
      await handleUpdateAddress();
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const addresses = [...(userProfile.addresses || [])];
      const newAddr = {
        ...newAddress,
        id: Date.now().toString(),
        isDefault: addresses.length === 0 ? true : newAddress.isDefault
      } as UserAddress;
      
      addresses.push(newAddr);
      
      // Set as default if it's the first address or if isDefault is true
      const defaultAddressId = addresses.length === 1 || newAddr.isDefault 
        ? newAddr.id 
        : userProfile.defaultAddressId;
      
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        addresses,
        defaultAddressId,
        updatedAt: new Date().toISOString()
      });
      
      await fetchUserProfile();
      setShowAddAddress(false);
      setNewAddress({
        fullName: '',
        phone: '',
        houseNumber: '',
        streetAddress: '',
        landmark: '',
        city: '',
        state: '',
        country: 'India',
        pincode: '',
        isDefault: false
      });
      setSuccessMessage('Address added successfully');
    } catch (err) {
      console.error('Error adding address:', err);
      setError('Failed to add address');
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefaultAddress = async (address: UserAddress) => {
    if (!user || !userProfile) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        defaultAddressId: address.id,
        updatedAt: new Date().toISOString()
      });
      
      await fetchUserProfile();
      setSuccessMessage('Default address updated');
    } catch (err) {
      console.error('Error setting default address:', err);
      setError('Failed to update default address');
    } finally {
      setLoading(false);
    }
  };

  const handleEditAddress = (address: UserAddress) => {
    setEditingAddress(address);
    setNewAddress({
      fullName: address.fullName,
      phone: address.phone,
      houseNumber: address.houseNumber,
      streetAddress: address.streetAddress,
      landmark: address.landmark,
      city: address.city,
      state: address.state,
      country: address.country,
      pincode: address.pincode,
      isDefault: address.id === userProfile?.defaultAddressId
    });
    setShowAddAddress(true);
  };

  const handleUpdateAddress = async () => {
    if (!user || !userProfile || !editingAddress) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const addresses = userProfile.addresses?.map(addr => 
        addr.id === editingAddress.id
          ? { ...newAddress, id: addr.id } as UserAddress
          : addr
      ) || [];
      
      const defaultAddressId = newAddress.isDefault 
        ? editingAddress.id 
        : userProfile.defaultAddressId;
      
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        addresses,
        defaultAddressId,
        updatedAt: new Date().toISOString()
      });
      
      await fetchUserProfile();
      setShowAddAddress(false);
      setEditingAddress(null);
      setNewAddress({
        fullName: '',
        phone: '',
        houseNumber: '',
        streetAddress: '',
        landmark: '',
        city: '',
        state: '',
        country: 'India',
        pincode: '',
        isDefault: false
      });
      setSuccessMessage('Address updated successfully');
    } catch (err) {
      console.error('Error updating address:', err);
      setError('Failed to update address');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAddress = async (address: UserAddress) => {
    if (!user || !userProfile) return;
    
    // Confirm before deleting
    if (!window.confirm('Are you sure you want to delete this address?')) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const addresses = userProfile.addresses?.filter(addr => addr.id !== address.id) || [];
      
      // If deleting the default address, set the first remaining address as default
      let defaultAddressId = userProfile.defaultAddressId;
      if (address.id === userProfile.defaultAddressId && addresses.length > 0) {
        defaultAddressId = addresses[0].id;
      } else if (addresses.length === 0) {
        defaultAddressId = undefined;
      }
      
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        addresses,
        defaultAddressId,
        updatedAt: new Date().toISOString()
      });
      
      await fetchUserProfile();
      setSuccessMessage('Address deleted successfully');
    } catch (err) {
      console.error('Error deleting address:', err);
      setError('Failed to delete address');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmail = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);
      await sendEmailVerification(user);
      setSuccessMessage('Verification email sent. Please check your inbox.');
    } catch (err) {
      console.error('Error sending verification email:', err);
      setError('Failed to send verification email');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!user || !user.email) return;
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);

      // First, reauthenticate the user
      const credential = EmailAuthProvider.credential(
        user.email,
        passwordForm.currentPassword
      );
      
      await reauthenticateWithCredential(user, credential);
      
      // Then update the password
      await updatePassword(user, passwordForm.newPassword);
      
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setSuccessMessage('Password updated successfully');
    } catch (err: any) {
      console.error('Error updating password:', err);
      if (err.code === 'auth/wrong-password') {
        setError('Current password is incorrect');
      } else if (err.code === 'auth/requires-recent-login') {
        setError('Please log out and log back in before changing your password');
      } else if (err.code === 'auth/weak-password') {
        setError('New password is too weak. It must be at least 6 characters long');
      } else {
        setError('Failed to update password. Please try again');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Left Sidebar */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-2xl shadow-lg p-6"
            >
              <div className="text-center mb-6">
                <div className="w-24 h-24 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4 overflow-hidden">
                  {userProfile?.profileImage ? (
                    <img
                      src={userProfile.profileImage}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="h-12 w-12 text-primary-600" />
                  )}
                </div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {userProfile?.fullName || 'Loading...'}
                </h2>
                <p className="text-gray-600">{userProfile?.email}</p>
              </div>

              <nav className="space-y-2">
                <button
                  onClick={() => setActiveTab('profile')}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    activeTab === 'profile'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <User className="h-5 w-5" />
                  <span>Profile</span>
                </button>

                <button
                  onClick={() => setActiveTab('addresses')}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    activeTab === 'addresses'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <MapPin className="h-5 w-5" />
                  <span>Addresses</span>
                </button>

                <button
                  onClick={() => setActiveTab('orders')}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    activeTab === 'orders'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <ShoppingBag className="h-5 w-5" />
                  <span>My Bookings</span>
                </button>

                <button
                  onClick={() => setActiveTab('reviews')}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    activeTab === 'reviews'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Star className="h-5 w-5" />
                  <span>Reviews</span>
                </button>

                <button
                  onClick={() => setActiveTab('settings')}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    activeTab === 'settings'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Settings className="h-5 w-5" />
                  <span>Settings</span>
                </button>
              </nav>

              {/* Stats */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="grid grid-cols-1 gap-4">
                  {/* User Rating */}
                  {userProfile?.averageRating !== undefined && userProfile.averageRating > 0 && (
                    <div className="bg-yellow-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm text-gray-600 mb-1">Your Rating</div>
                          <div className="flex items-center space-x-2">
                            <div className="text-3xl font-bold text-yellow-600">
                              {userProfile.averageRating.toFixed(1)}
                            </div>
                            <Star className="h-6 w-6 text-yellow-500 fill-yellow-500" />
                          </div>
                        </div>
                        <Star className="h-10 w-10 text-yellow-500 opacity-50" />
                      </div>
                    </div>
                  )}

                  {/* Total Bookings */}
                  <div className="bg-primary-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm text-gray-600 mb-1">Total Bookings</div>
                        <div className="text-3xl font-bold text-primary-600">
                          {orders.length || 0}
                        </div>
                      </div>
                      <ShoppingBag className="h-10 w-10 text-primary-600 opacity-50" />
                    </div>
                  </div>

                  {/* Latest/Upcoming Booking */}
                  {orders.length > 0 && (() => {
                    // Find the next upcoming order (only 'placed' status, with future date)
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    
                    const upcomingOrders = orders.filter(order => {
                      // Only show bookings with 'placed' status (Booking Requested)
                      if (order.status !== 'placed') {
                        return false;
                      }
                      
                      // Check if any item has a future date
                      if (!order.items || order.items.length === 0) {
                        return false;
                      }
                      
                      try {
                        return order.items.some((item: any) => {
                          if (!item.scheduling?.date) return false;
                          const itemDate = new Date(item.scheduling.date);
                          return itemDate >= today;
                        });
                      } catch (e) {
                        return false;
                      }
                    }).sort((a, b) => {
                      // Sort by earliest item date
                      const aDate = Math.min(...a.items
                        .filter((i: any) => i.scheduling?.date)
                        .map((i: any) => new Date(i.scheduling.date).getTime()));
                      const bDate = Math.min(...b.items
                        .filter((i: any) => i.scheduling?.date)
                        .map((i: any) => new Date(i.scheduling.date).getTime()));
                      return aDate - bDate;
                    });
                    
                    // If no upcoming orders, show the most recent booking
                    const displayOrder = upcomingOrders.length > 0 
                      ? upcomingOrders[0] 
                      : orders[0];
                    const isUpcoming = upcomingOrders.length > 0;
                    
                    return (
                      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
                        <div className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                          <Clock className="h-4 w-4 mr-2 text-blue-600" />
                          {isUpcoming ? 'Upcoming Booking' : 'Latest Booking'}
                        </div>
                        <div className="space-y-2">
                          <div className="font-medium text-gray-900 truncate">
                            {displayOrder.items?.[0]?.name || 'Service'}
                            {displayOrder.items?.length > 1 && ` +${displayOrder.items.length - 1} more`}
                          </div>
                          <div className="flex items-center justify-between">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              displayOrder.status === 'completed'
                                ? 'bg-green-100 text-green-800'
                                : displayOrder.status === 'cancelled'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {displayOrder.status === 'completed' ? 'Completed' :
                               displayOrder.status === 'cancelled' ? 'Cancelled' :
                               'Booking Requested'}
                            </span>
                            <span className="text-sm text-gray-600">
                              {(() => {
                                const dates = displayOrder.items
                                  ?.filter((i: any) => i.scheduling?.date)
                                  .map((i: any) => i.scheduling.date);
                                return dates && dates.length > 0 ? dates[0] : 'Date not set';
                              })()}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* No Bookings Message */}
                  {orders.length === 0 && (
                    <div className="bg-gray-50 rounded-lg p-4 text-center border border-gray-200">
                      <ShoppingBag className="h-10 w-10 text-gray-400 mx-auto mb-2" />
                      <div className="text-sm text-gray-600">No bookings yet</div>
                      <div className="text-xs text-gray-500 mt-1">Start booking services!</div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <LoadingSpinner />
              </div>
            ) : (
              <>
                {/* Success Message */}
                {successMessage && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6 p-4 bg-green-50 text-green-700 rounded-lg flex items-center"
                  >
                    <Check className="h-5 w-5 mr-2" />
                    <p>{successMessage}</p>
                  </motion.div>
                )}

                {/* Error Message */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg flex items-center"
                  >
                    <AlertCircle className="h-5 w-5 mr-2" />
                    <p>{error}</p>
                  </motion.div>
                )}

                {/* Profile Completion Warning */}
                {showProfileWarning && activeTab === 'profile' && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6 p-4 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-lg flex items-center justify-between"
                  >
                    <div className="flex items-center">
                      <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">Complete Your Profile</p>
                        <p className="text-sm text-yellow-700">
                          Please add your full name, phone number, and at least one address before booking services.
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowProfileWarning(false)}
                      className="ml-4 text-yellow-600 hover:text-yellow-700"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </motion.div>
                )}

                {/* Profile Tab */}
                {activeTab === 'profile' && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-2xl shadow-lg p-8"
                  >
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-2xl font-semibold">Profile Information</h2>
                      <button
                        onClick={() => setIsEditing(!isEditing)}
                        className="flex items-center text-primary-600 hover:text-primary-700"
                      >
                        <Edit2 className="h-4 w-4 mr-1" />
                        {isEditing ? 'Cancel' : 'Edit'}
                      </button>
                    </div>

                    {/* Profile Photo Section */}
                    <div className="mb-8 flex flex-col items-center">
                      <div className="relative">
                        <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                          {userProfile?.profileImage || user?.photoURL ? (
                            <img 
                              src={userProfile?.profileImage || user?.photoURL || ''} 
                              alt="Profile" 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <User className="w-16 h-16 text-gray-400" />
                          )}
                        </div>
                        <label 
                          htmlFor="photo-upload" 
                          className="absolute bottom-0 right-0 bg-primary-600 text-white p-2 rounded-full cursor-pointer hover:bg-primary-700 transition-colors"
                        >
                          {uploadingPhoto ? (
                            <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                          ) : (
                            <Camera className="h-5 w-5" />
                          )}
                        </label>
                        <input
                          id="photo-upload"
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoUpload}
                          className="hidden"
                          disabled={uploadingPhoto}
                        />
                      </div>
                      <p className="mt-2 text-sm text-gray-500">Click camera icon to upload photo</p>
                    </div>

                    {isEditing ? (
                      <form onSubmit={(e) => { e.preventDefault(); handleUpdateProfile(); }}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Full Name
                            </label>
                            <input
                              type="text"
                              value={editForm.fullName}
                              onChange={(e) => setEditForm({ ...editForm, fullName: e.target.value })}
                              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Phone Number
                            </label>
                            <input
                              type="tel"
                              value={editForm.phoneNumber}
                              onChange={(e) => setEditForm({ ...editForm, phoneNumber: e.target.value })}
                              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
                              pattern="[0-9]{10}"
                              title="Please enter a valid 10-digit phone number"
                              required
                            />
                          </div>
                        </div>
                        <div className="mt-6">
                          <button
                            type="submit"
                            className="w-full md:w-auto px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                          >
                            Save Changes
                          </button>
                        </div>
                      </form>
                    ) : (
                      <div className="space-y-6">
                        <div className="flex items-center">
                          <User className="h-5 w-5 text-gray-400 mr-3" />
                          <div className="flex-1">
                            <div className="text-sm text-gray-500">Full Name</div>
                            <div className="text-gray-900 font-medium">{userProfile?.fullName || 'Not provided'}</div>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <Mail className="h-5 w-5 text-gray-400 mr-3" />
                          <div className="flex-1">
                            <div className="text-sm text-gray-500">Email</div>
                            <div className="flex items-center">
                              <span className="text-gray-900">{userProfile?.email}</span>
                              {userProfile?.isEmailVerified ? (
                                <span className="ml-2 text-green-600 text-sm flex items-center">
                                  <Check className="h-4 w-4 mr-1" />
                                  Verified
                                </span>
                              ) : (
                                <button
                                  onClick={handleVerifyEmail}
                                  className="ml-2 text-sm text-primary-600 hover:text-primary-700 flex items-center"
                                >
                                  <Send className="h-4 w-4 mr-1" />
                                  Verify Email
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <Phone className="h-5 w-5 text-gray-400 mr-3" />
                          <div className="flex-1">
                            <div className="text-sm text-gray-500">Phone Number</div>
                            <div className="text-gray-900 font-medium">
                              {userProfile?.phoneNumber || 'Not provided'}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}

                {/* Addresses Tab */}
                {activeTab === 'addresses' && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-6"
                  >
                    <div className="flex justify-between items-center">
                      <h2 className="text-2xl font-semibold">My Addresses</h2>
                      <button
                        onClick={() => setShowAddAddress(true)}
                        className="flex items-center text-primary-600 hover:text-primary-700"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add New Address
                      </button>
                    </div>

                    {showAddAddress && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white rounded-2xl shadow-lg p-8"
                      >
                        <h3 className="text-xl font-semibold mb-6">
                          {editingAddress ? 'Edit Address' : 'Add New Address'}
                        </h3>
                        <form onSubmit={(e) => { e.preventDefault(); handleAddAddress(); }}>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <label className="block text-sm font-medium text-gray-700">
                                Full Name *
                              </label>
                              <input
                                type="text"
                                value={newAddress.fullName}
                                onChange={(e) => setNewAddress({ ...newAddress, fullName: e.target.value })}
                                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">
                                Phone Number *
                              </label>
                              <input
                                type="tel"
                                value={newAddress.phone}
                                onChange={(e) => setNewAddress({ ...newAddress, phone: e.target.value })}
                                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
                                pattern="[0-9]{10}"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">
                                House Number *
                              </label>
                              <input
                                type="text"
                                value={newAddress.houseNumber}
                                onChange={(e) => setNewAddress({ ...newAddress, houseNumber: e.target.value })}
                                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">
                                Street Address
                              </label>
                              <input
                                type="text"
                                value={newAddress.streetAddress}
                                onChange={(e) => setNewAddress({ ...newAddress, streetAddress: e.target.value })}
                                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">
                                Landmark *
                              </label>
                              <input
                                type="text"
                                value={newAddress.landmark}
                                onChange={(e) => setNewAddress({ ...newAddress, landmark: e.target.value })}
                                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">
                                City *
                              </label>
                              <input
                                type="text"
                                value={newAddress.city}
                                onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
                                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">
                                State *
                              </label>
                              <input
                                type="text"
                                value={newAddress.state}
                                onChange={(e) => setNewAddress({ ...newAddress, state: e.target.value })}
                                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">
                                Pincode *
                              </label>
                              <input
                                type="text"
                                value={newAddress.pincode}
                                onChange={(e) => setNewAddress({ ...newAddress, pincode: e.target.value })}
                                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
                                pattern="[0-9]{6}"
                                required
                              />
                            </div>
                          </div>
                          <div className="mt-6 flex items-center">
                            <input
                              type="checkbox"
                              id="isDefault"
                              checked={newAddress.isDefault}
                              onChange={(e) => setNewAddress({ ...newAddress, isDefault: e.target.checked })}
                              className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                            />
                            <label htmlFor="isDefault" className="ml-2 text-sm text-gray-700">
                              Set as default address
                            </label>
                          </div>
                          <div className="mt-6 flex justify-end space-x-4">
                            <button
                              type="button"
                              onClick={() => {
                                setShowAddAddress(false);
                                setEditingAddress(null);
                                setNewAddress({
                                  fullName: '',
                                  phone: '',
                                  houseNumber: '',
                                  streetAddress: '',
                                  landmark: '',
                                  city: '',
                                  state: '',
                                  country: 'India',
                                  pincode: '',
                                  isDefault: false
                                });
                              }}
                              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                            >
                              {editingAddress ? 'Update Address' : 'Save Address'}
                            </button>
                          </div>
                        </form>
                      </motion.div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {userProfile?.addresses?.map((address) => (
                        <motion.div
                          key={address.id}
                          layout
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className={`bg-white rounded-2xl shadow-lg p-6 ${
                            address.id === userProfile.defaultAddressId ? 'ring-2 ring-primary-500' : ''
                          }`}
                        >
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h3 className="font-semibold text-gray-900">{address.fullName}</h3>
                              <p className="text-gray-600">{address.phone}</p>
                            </div>
                            {address.id === userProfile.defaultAddressId ? (
                              <div className="flex items-center space-x-1">
                                <Check className="h-4 w-4 text-green-600" />
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  Default Address
                                </span>
                              </div>
                            ) : (
                              <button
                                onClick={() => handleSetDefaultAddress(address)}
                                className="text-sm text-primary-600 hover:text-primary-700 flex items-center space-x-1"
                              >
                                <MapPin className="h-4 w-4" />
                                <span>Set as Default</span>
                              </button>
                            )}
                          </div>
                          <p className="text-gray-600 mb-4">
                            {[
                              address.houseNumber,
                              address.streetAddress,
                              address.landmark,
                              address.city,
                              address.state,
                              address.pincode
                            ]
                              .filter(Boolean)
                              .join(', ')}
                          </p>
                          
                          {/* Action Buttons */}
                          <div className="flex items-center space-x-3 pt-4 border-t border-gray-100">
                            <button
                              onClick={() => handleEditAddress(address)}
                              className="flex items-center text-sm text-blue-600 hover:text-blue-700 transition-colors"
                            >
                              <Edit2 className="h-4 w-4 mr-1" />
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteAddress(address)}
                              className="flex items-center text-sm text-red-600 hover:text-red-700 transition-colors"
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Delete
                            </button>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Orders/Bookings Tab */}
                {activeTab === 'orders' && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-6"
                  >
                    <div className="bg-white rounded-2xl shadow-lg p-4 md:p-8">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
                        <h2 className="text-xl md:text-2xl font-semibold">My Bookings</h2>
                        <div className="text-sm text-gray-600">
                          Total: {orders.length} booking{orders.length !== 1 ? 's' : ''}
                        </div>
                      </div>

                      {ordersLoading ? (
                        <div className="flex justify-center items-center h-64">
                          <LoadingSpinner />
                        </div>
                      ) : orders.length === 0 ? (
                        <div className="text-center py-12">
                          <ShoppingBag className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 mb-2">No bookings yet</h3>
                          <p className="text-gray-600 mb-6">Start booking services to see them here</p>
                          <a
                            href="/services"
                            className="inline-flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                          >
                            Browse Services
                          </a>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {orders.map((order) => (
                            <motion.div
                              key={order.id}
                              id={`order-${order.id}`}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
                            >
                              {/* Compact Header */}
                              <div className="bg-primary-600 px-4 py-2.5 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="font-bold text-white text-sm">#{order.id.slice(-8).toUpperCase()}</span>
                                  {/* Show payment status using unified tracking */}
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                    isOrderPaid(order) ? 'bg-green-500 text-white' :
                                    'bg-yellow-500 text-white'
                                  }`}>
                                    {isOrderPaid(order) ? '✅ Paid' : '⚠️ Payment Due'}
                                  </span>
                                </div>
                                <div className="flex items-center gap-3 text-white">
                                  <span className="text-sm">
                                    {order.createdAt ? new Date(order.createdAt).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric'
                                    }) : ''}
                                  </span>
                                  <div className="ml-auto">
                                    <span className="text-xl font-bold">
                                      ₹{(order.grandTotal || order.total || order.items?.reduce((sum: number, item: any) => sum + (item.payment?.total || item.price * item.quantity), 0) || 0).toLocaleString()}
                                    </span>
                                    <span className="text-xs ml-1 opacity-80">
                                      {getPaymentMethodDisplay(order).includes('PhonePe') ? 'PhonePe' :
                                       getPaymentMethodDisplay(order).includes('Pay After') ? 'PAS' :
                                       order.items?.[0]?.payment?.method === 'upi' ? 'UPI' :
                                       order.items?.[0]?.payment?.method === 'phonepe' ? 'PhonePe' :
                                       order.items?.[0]?.payment?.method === 'pas' ? 'PAS' : ''}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Compact Content */}
                              <div className="p-4 space-y-3">
                                
                                {/* User Cancellation Alert */}
                                {order.cancelledBy === 'user' && order.cancelReason && (
                                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-start gap-2">
                                    <AlertCircle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                                    <div className="flex-1">
                                      <p className="text-sm font-semibold text-orange-900 mb-1">Order Cancelled by You</p>
                                      <p className="text-xs text-orange-700">{order.cancelReason}</p>
                                      {order.cancelledAt && (
                                        <p className="text-xs text-orange-600 mt-1">
                                          Cancelled on: {new Date(order.cancelledAt).toLocaleString('en-IN')}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Payment Failure Alert */}
                                {order.items?.some((item: any) => item.payment?.status === 'failed') && order.payment?.failureReason && !order.cancelledBy && (
                                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                                    <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                                    <div className="flex-1">
                                      <p className="text-sm font-semibold text-red-900 mb-1">Payment Failed</p>
                                      <p className="text-xs text-red-700">{order.payment.failureReason}</p>
                                      <button
                                        onClick={() => navigate(`/checkout?retry=${order.id}`)}
                                        className="mt-2 text-xs bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition-colors"
                                      >
                                        Retry Payment
                                      </button>
                                    </div>
                                  </div>
                                )}
                                
                                {/* Services */}
                                <div>
                                  <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                                    <ShoppingBag className="h-4 w-4" />
                                    Services
                                  </h4>
                                  {order.items?.map((item: any, index: number) => (
                                    <div key={index} className="p-3 bg-gray-50 rounded-lg border border-gray-200 mb-2">
                                      <div className="flex gap-3 mb-2">
                                        <img
                                          src={item.image || '/placeholder.jpg'}
                                          alt={item.name}
                                          className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                                        />
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-start justify-between mb-1">
                                            <h5 className="font-semibold text-gray-900 text-sm">{item.name}</h5>
                                            {item.status && (
                                              <span 
                                                className={`px-2 py-0.5 rounded-full text-xs font-medium ml-2 ${
                                                  item.status === 'completed' ? 'bg-green-100 text-green-700' :
                                                  item.status === 'active' ? 'bg-purple-100 text-purple-700' :
                                                  item.status === 'confirmed' ? 'bg-blue-100 text-blue-700' :
                                                  item.status === 'cancelled' ? 'bg-orange-100 text-orange-700' :
                                                  'bg-gray-100 text-gray-700'
                                                }`}
                                                title={item.status === 'cancelled' && item.cancelReason ? `Reason: ${item.cancelReason}` : ''}
                                              >
                                                {item.status === 'completed' ? 'Completed' :
                                                 item.status === 'active' ? 'In Progress' :
                                                 item.status === 'confirmed' ? 'Confirmed' :
                                                 item.status === 'cancelled' ? 'Cancelled' :
                                                 'Placed'}
                                              </span>
                                            )}
                                          </div>
                                          <div className="flex items-center gap-3 text-xs text-gray-600">
                                            <span>Qty: {item.quantity}</span>
                                            <span>₹{item.price?.toLocaleString()}</span>
                                            {item.duration && <span>{item.duration}</span>}
                                          </div>
                                          {item.addons && item.addons.length > 0 && (
                                            <div className="mt-2 pt-2 border-t border-gray-300">
                                              <div className="text-xs font-medium text-gray-700 mb-1">Add-ons:</div>
                                              {item.addons.map((addon: any, addonIndex: number) => (
                                                <div key={addonIndex} className="flex items-center justify-between text-xs">
                                                  <span className="text-gray-600">• {addon.name}</span>
                                                  {addon.price && <span className="text-primary-600 font-medium">+₹{addon.price}</span>}
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      
                                      {/* Provider info for confirmed items */}
                                      {item.providerName && (
                                        <div className="mt-2 pt-2 border-t border-gray-300">
                                          <div className="flex items-center gap-2 text-xs">
                                            <span className="font-medium text-gray-700">Provider:</span>
                                            <span className="text-gray-600">{item.providerName}</span>
                                            {item.providerPhone && (
                                              <span className="text-gray-500">({item.providerPhone})</span>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Item-specific scheduling and payment */}
                                      {item.scheduling && (
                                        <div className="mt-2 pt-2 border-t border-gray-300 grid grid-cols-2 gap-2 text-xs">
                                          <div className="flex items-center gap-1 text-gray-600">
                                            <Clock className="h-3 w-3" />
                                            <span>{item.scheduling.date} • {item.scheduling.time}</span>
                                          </div>
                                          {item.payment && (
                                            <div className="flex items-center gap-1 justify-end">
                                              <span className="font-medium text-gray-900">₹{item.payment.total?.toLocaleString()}</span>
                                              <span className={`px-1.5 py-0.5 rounded text-xs ${
                                                item.payment.status === 'completed' ? 'bg-green-100 text-green-700' :
                                                item.payment.status === 'failed' ? 'bg-red-100 text-red-700' :
                                                'bg-yellow-100 text-yellow-700'
                                              }`} title={item.payment.status === 'failed' && item.payment.failureReason ? item.payment.failureReason : ''}>
                                                {item.payment.status === 'completed' ? 'Paid' :
                                                 item.payment.status === 'failed' ? 'Failed' : 'Pending'}
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>

                                {/* Address */}
                                {order.address && (
                                  <div>
                                    <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                                      <MapPin className="h-4 w-4" />
                                      Address
                                    </h4>
                                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                      <p className="font-medium text-gray-900 text-sm">{order.address.fullName}</p>
                                      <p className="text-xs text-gray-600 mt-0.5">{order.address.phone}</p>
                                      <p className="text-xs text-gray-600 mt-1">
                                        {[order.address.houseNumber, order.address.streetAddress, order.address.city, order.address.pincode].filter(Boolean).join(', ')}
                                      </p>
                                    </div>
                                  </div>
                                )}

                              {/* Service Provider & Timeline */}
                              {(order.status === 'confirmed' || order.status === 'active' || order.status === 'in-progress' || order.status === 'partial-completed' || order.status === 'completed') && 
                               (order.providerName || order.providerPhone) && (
                                <div>
                                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Service Provider</h4>
                                  <div className="bg-primary-50 rounded-lg p-3 border border-primary-200">
                                    <div className="flex items-center gap-2">
                                      <div className="w-10 h-10 rounded-full bg-primary-600 flex items-center justify-center text-white font-bold text-sm">
                                        {order.providerName ? order.providerName.charAt(0).toUpperCase() : 'P'}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        {order.providerName && (
                                          <p className="font-medium text-gray-900 text-sm">{order.providerName}</p>
                                        )}
                                        {order.providerPhone && (
                                          <p className="text-xs text-gray-600">{order.providerPhone}</p>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* Complete Timeline with Times */}
                                    {order.createdAt && (
                                      <div className="mt-3 pt-3 border-t border-primary-200 space-y-2 text-xs">
                                        {/* Debug log */}
                                        {console.log('📅 Timeline Debug:', {
                                          orderId: order.id,
                                          status: order.status,
                                          startedAt: order.startedAt,
                                          serviceStartTime: order.serviceStartTime,
                                          hasStarted: !!(order.startedAt || order.serviceStartTime)
                                        })}
                                        
                                        <div className="flex items-start gap-2 text-gray-700">
                                          <div className="w-2 h-2 rounded-full bg-blue-500 mt-1"></div>
                                          <div className="flex-1">
                                            <div className="font-semibold">Order Placed</div>
                                            <div className="text-gray-500">
                                              {new Date(order.createdAt).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})} at {new Date(order.createdAt).toLocaleTimeString('en-US', {hour: '2-digit', minute: '2-digit'})}
                                            </div>
                                          </div>
                                        </div>
                                        {(order.acceptedAt || order.confirmedAt) && (
                                          <div className="flex items-start gap-2 text-gray-700">
                                            <div className="w-2 h-2 rounded-full bg-indigo-500 mt-1"></div>
                                            <div className="flex-1">
                                              <div className="font-semibold">Order Confirmed</div>
                                              <div className="text-gray-500">
                                                {(() => {
                                                  const date = order.acceptedAt || order.confirmedAt;
                                                  const dateObj = date?.toDate ? date.toDate() : new Date(date);
                                                  return `${dateObj.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})} at ${dateObj.toLocaleTimeString('en-US', {hour: '2-digit', minute: '2-digit'})}`;
                                                })()}
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                        {(order.status === 'active' || order.status === 'in-progress' || order.status === 'partial-completed' || order.status === 'completed') && (order.startedAt || order.serviceStartTime) && (
                                          <div className="flex items-start gap-2 text-gray-700">
                                            <div className="w-2 h-2 rounded-full bg-purple-500 mt-1"></div>
                                            <div className="flex-1">
                                              <div className="font-semibold">Service Started</div>
                                              <div className="text-gray-500">
                                                {(() => {
                                                  const date = order.startedAt || order.serviceStartTime;
                                                  const dateObj = date?.toDate ? date.toDate() : new Date(date);
                                                  return `${dateObj.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})} at ${dateObj.toLocaleTimeString('en-US', {hour: '2-digit', minute: '2-digit'})}`;
                                                })()}
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                        {order.status === 'completed' && (order.completedAt || order.serviceCompletedTime) && (
                                          <div className="flex items-start gap-2 text-gray-700">
                                            <div className="w-2 h-2 rounded-full bg-green-500 mt-1"></div>
                                            <div className="flex-1">
                                              <div className="font-semibold">Service Completed</div>
                                              <div className="text-gray-500">
                                                {(() => {
                                                  const date = order.completedAt || order.serviceCompletedTime;
                                                  const dateObj = date?.toDate ? date.toDate() : new Date(date);
                                                  return `${dateObj.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})} at ${dateObj.toLocaleTimeString('en-US', {hour: '2-digit', minute: '2-digit'})}`;
                                                })()}
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                                {/* Payment */}
                                <div className="pt-3 border-t border-gray-200">
                                  <div className="flex items-center justify-between text-sm mb-1">
                                    <span className="text-gray-600">Subtotal</span>
                                    <span className="font-medium">₹{order.payment?.subtotal?.toLocaleString() || '0'}</span>
                                  </div>
                                  
                                  {/* Additional Charges if exist */}
                                  {order.additionalCharges && order.additionalCharges.length > 0 && (
                                    <div className="bg-yellow-50 rounded-lg p-2 my-2 border border-yellow-200">
                                      <div className="text-xs font-semibold text-yellow-800 mb-1">Additional Charges:</div>
                                      {order.additionalCharges.map((charge: any, idx: number) => (
                                        <div key={idx} className="flex items-center justify-between text-xs text-gray-700">
                                          <span>• {charge.description || charge.name}</span>
                                          <span className="font-medium">+₹{charge.amount?.toLocaleString() || '0'}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  
                                  <div className="flex items-center justify-between font-bold text-base">
                                    <span>Total</span>
                                    <span className="text-primary-600">₹{(
                                      order.finalAmount || 
                                      (order.payment?.total || 0) + 
                                      (Array.isArray(order.additionalCharges) ? order.additionalCharges.reduce((sum: number, c: any) => sum + (c.amount || 0), 0) : 0)
                                    ).toLocaleString()}</span>
                                  </div>
                                  
                                  {/* Payment Status */}
                                  {(() => {
                                    const unpaidAmount = calculateUnpaidAmount(order);
                                    const isPaid = unpaidAmount === 0;
                                    
                                    return (
                                      <div className="flex items-center justify-between text-xs mt-2 pt-2 border-t border-gray-200">
                                        <span className="text-gray-600">Payment Status:</span>
                                        {isPaid ? (
                                          <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-800 font-medium">
                                            <Check className="h-3 w-3 mr-1" />
                                            Fully Paid
                                          </span>
                                        ) : (
                                          <span className="inline-flex items-center px-2 py-1 rounded-full bg-yellow-100 text-yellow-800 font-medium">
                                            <Clock className="h-3 w-3 mr-1" />
                                            ₹{unpaidAmount.toLocaleString()} Pending
                                          </span>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>
                              </div>

                              {/* Pay Now Button - Show when there's unpaid amount */}
                              {(() => {
                                const unpaidAmount = calculateUnpaidAmount(order);
                                const isPAS = (order.payment?.method || '').toLowerCase() === 'pas';
                                const hasAdditionalServices = (order.additionalTotal || 0) > 0 || (order.additionalServices?.length || 0) > 0;
                                
                                // Show Pay Now for:
                                // 1. Completed orders with PAS payment method
                                // 2. Any order with additional services added
                                // 3. Orders that have unpaid amount
                                const showPayNow = unpaidAmount > 0 && (
                                  order.status === 'completed' || 
                                  order.status === 'partial-completed' ||
                                  (order.status === 'active' && hasAdditionalServices)
                                );
                                
                                return showPayNow && (
                                  <div className={`px-4 py-3 border-t-2 ${
                                    order.status === 'partial-completed' 
                                      ? 'bg-gradient-to-r from-orange-50 to-red-50 border-orange-500 animate-pulse'
                                      : 'bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-400'
                                  }`}>
                                    <div className="flex items-center justify-between mb-2">
                                      <div>
                                        <div className="text-sm font-semibold text-gray-900">
                                          {order.status === 'partial-completed' ? '⚠️ Payment Required to Complete Service' : 
                                           order.status === 'active' ? '💳 Additional Services Added' :
                                           isPAS ? '💵 Pay After Service' : 'Payment Pending'}
                                        </div>
                                        <div className="text-xs text-gray-600">
                                          {order.status === 'partial-completed' 
                                            ? 'Service partially completed. Pay now to proceed with completion.'
                                            : isPAS && !hasAdditionalServices
                                            ? 'Pay for your completed service'
                                            : hasAdditionalServices
                                            ? `Original: ₹${(order.total || 0).toLocaleString()} + Additional: ₹${(order.additionalTotal || 0).toLocaleString()}`
                                            : 'Payment required to download invoice'}
                                        </div>
                                      </div>
                                      <div className="text-right">
                                        <div className="text-xs text-gray-600">Amount Due</div>
                                        <div className="text-lg font-bold text-orange-600">₹{unpaidAmount.toLocaleString()}</div>
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => handlePayNow(order)}
                                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 transition-all font-bold text-sm shadow-lg hover:shadow-xl"
                                    >
                                      <IndianRupee className="h-5 w-5" />
                                      Pay Now - ₹{unpaidAmount.toLocaleString()}
                                    </button>
                                  </div>
                                );
                              })()}

                              {/* Action Buttons */}
                              <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
                                {(() => {
                                  const showInvoice = order.status === 'completed';
                                  const showFeedback = order.status === 'completed' && !order.feedbackSubmitted;
                                  
                                  // Calculate grid columns dynamically
                                  let buttonCount = 2; // Rebook + Share (always shown)
                                  if (showInvoice) buttonCount++;
                                  if (showFeedback) buttonCount++;
                                  
                                  const gridCols = buttonCount === 4 ? 'sm:grid-cols-4' : 
                                                   buttonCount === 3 ? 'sm:grid-cols-3' : 
                                                   'sm:grid-cols-2';
                                  
                                  return (
                                    <div className={`grid grid-cols-1 gap-3 ${gridCols}`}>
                                      {/* Rebook Button */}
                                      <button
                                        onClick={() => handleRebook(order)}
                                        className="flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors font-medium text-sm"
                                      >
                                        <RefreshCw className="h-4 w-4" />
                                        Rebook
                                      </button>

                                      {/* Download Invoice Button - Only show when service is fully completed */}
                                      {(showInvoice && order.status !== 'partial-completed') && (() => {
                                        const unpaidAmount = calculateUnpaidAmount(order);
                                        const isFullyPaid = unpaidAmount === 0;
                                        
                                        return (
                                          <button
                                            onClick={() => isFullyPaid && handleDownloadInvoice(order)}
                                            disabled={!isFullyPaid}
                                            className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg transition-colors font-medium text-sm ${
                                              isFullyPaid
                                                ? 'bg-green-50 text-green-600 hover:bg-green-100 cursor-pointer'
                                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                            }`}
                                            title={isFullyPaid ? 'Download Invoice' : 'Complete payment to download invoice'}
                                          >
                                            <Download className="h-4 w-4" />
                                            Invoice
                                          </button>
                                        );
                                      })()}

                                      {/* Share Button */}
                                      <button
                                        onClick={() => handleShareOrder(order)}
                                        className="flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors font-medium text-sm"
                                      >
                                        <Share2 className="h-4 w-4" />
                                        Share
                                      </button>

                                      {/* Give Feedback Button - Only for completed orders without feedback */}
                                      {showFeedback && (
                                        <button
                                          onClick={() => {
                                            setFeedbackOrder(order);
                                            setShowFeedbackModal(true);
                                          }}
                                          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition-colors font-medium text-sm"
                                        >
                                          <Star className="h-4 w-4" />
                                          Feedback
                                        </button>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>

                              {/* Cancel Button - Only show for "Booking Requested" status */}
                              {(order.status === 'placed' || order.status === 'pending') && (
                                <div className="border-t border-gray-200 pt-4 mt-4">
                                  <button
                                    onClick={() => handleCancelClick(order)}
                                    className="w-full px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors font-medium text-sm md:text-base"
                                  >
                                    Cancel Booking
                                  </button>
                                </div>
                              )}
                            </motion.div>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}

                {/* Reviews Tab */}
                {activeTab === 'reviews' && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-6"
                  >
                    <div className="bg-white rounded-2xl shadow-lg p-4 md:p-8">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
                        <h2 className="text-xl md:text-2xl font-semibold">Provider Reviews</h2>
                        <div className="text-sm text-gray-600">
                          Total: {reviews.length} review{reviews.length !== 1 ? 's' : ''}
                        </div>
                      </div>

                      {reviewsLoading ? (
                        <div className="flex justify-center items-center h-64">
                          <LoadingSpinner />
                        </div>
                      ) : reviews.length === 0 ? (
                        <div className="text-center py-12">
                          <MessageSquare className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 mb-2">No reviews yet</h3>
                          <p className="text-gray-600 mb-6">Service providers will leave feedback here after completing your bookings</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {reviews.map((review) => (
                            <motion.div
                              key={review.id}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                              onClick={() => {
                                // Navigate to the order in My Bookings
                                if (review.orderId) {
                                  setActiveTab('orders');
                                  // Scroll to order after a brief delay
                                  setTimeout(() => {
                                    const orderElement = document.getElementById(`order-${review.orderId}`);
                                    if (orderElement) {
                                      orderElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                      orderElement.classList.add('ring-4', 'ring-primary-400', 'ring-opacity-50');
                                      setTimeout(() => {
                                        orderElement.classList.remove('ring-4', 'ring-primary-400', 'ring-opacity-50');
                                      }, 3000);
                                    }
                                  }, 100);
                                }
                              }}
                            >
                              {/* Review Header */}
                              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-4 py-3">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    {/* Provider Avatar */}
                                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center overflow-hidden">
                                      {review.provider?.profileImage ? (
                                        <img
                                          src={review.provider.profileImage}
                                          alt={review.provider.name}
                                          className="w-full h-full object-cover"
                                        />
                                      ) : (
                                        <User className="h-6 w-6 text-purple-600" />
                                      )}
                                    </div>
                                    <div>
                                      <p className="text-white font-semibold">
                                        {review.provider?.name || 'Service Provider'}
                                      </p>
                                      <p className="text-purple-100 text-sm">
                                        {review.order?.serviceName || 'Service'}
                                      </p>
                                    </div>
                                  </div>
                                  
                                  {/* Rating */}
                                  <div className="flex items-center gap-1 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full">
                                    <Star className="h-5 w-5 text-yellow-300 fill-yellow-300" />
                                    <span className="text-white font-bold text-lg">
                                      {review.rating || 0}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Review Content */}
                              <div className="p-4">
                                {/* Order Info */}
                                <div className="flex flex-wrap items-center gap-3 mb-3 text-sm text-gray-600">
                                  <div className="flex items-center gap-1">
                                    <ShoppingBag className="h-4 w-4" />
                                    <span>Order #{review.orderId?.slice(-8).toUpperCase() || 'N/A'}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Clock className="h-4 w-4" />
                                    <span>
                                      {review.createdAt 
                                        ? new Date(review.createdAt).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                          })
                                        : 'N/A'}
                                    </span>
                                  </div>
                                  {review.order?.totalAmount && (
                                    <div className="flex items-center gap-1">
                                      <Wallet className="h-4 w-4" />
                                      <span>₹{review.order.totalAmount}</span>
                                    </div>
                                  )}
                                </div>

                                {/* Feedback Text */}
                                {review.feedback && (
                                  <div className="mb-3">
                                    <p className="text-gray-700 leading-relaxed">
                                      {review.feedback}
                                    </p>
                                  </div>
                                )}

                                {/* Cooperation Level */}
                                {review.cooperationLevel && (
                                  <div className="flex items-center gap-2 mb-3">
                                    <span className="text-sm text-gray-600">Cooperation:</span>
                                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                      review.cooperationLevel === 'excellent' ? 'bg-green-100 text-green-800' :
                                      review.cooperationLevel === 'good' ? 'bg-blue-100 text-blue-800' :
                                      review.cooperationLevel === 'average' ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-gray-100 text-gray-800'
                                    }`}>
                                      {review.cooperationLevel.charAt(0).toUpperCase() + review.cooperationLevel.slice(1)}
                                    </span>
                                  </div>
                                )}

                                {/* Provider Contact */}
                                {review.provider && (
                                  <div className="pt-3 border-t border-gray-200 flex items-center justify-between">
                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                      {review.provider.phone && (
                                        <div className="flex items-center gap-1">
                                          <Phone className="h-4 w-4" />
                                          <span>{review.provider.phone}</span>
                                        </div>
                                      )}
                                      {review.provider.rating > 0 && (
                                        <div className="flex items-center gap-1">
                                          <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                                          <span>{review.provider.rating.toFixed(1)}</span>
                                        </div>
                                      )}
                                    </div>
                                    
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (review.orderId) {
                                          setActiveTab('orders');
                                          setTimeout(() => {
                                            const orderElement = document.getElementById(`order-${review.orderId}`);
                                            if (orderElement) {
                                              orderElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                            }
                                          }, 100);
                                        }
                                      }}
                                      className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center gap-1"
                                    >
                                      View Order
                                      <ShoppingBag className="h-4 w-4" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}

                {/* Settings Tab */}
                {activeTab === 'settings' && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-6"
                  >
                    {/* Change Password */}
                    <div className="bg-white rounded-2xl shadow-lg p-8">
                      <h2 className="text-2xl font-semibold mb-6">Security Settings</h2>
                      <form onSubmit={(e) => { e.preventDefault(); handleUpdatePassword(); }}>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Current Password
                            </label>
                            <input
                              type="password"
                              value={passwordForm.currentPassword}
                              onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              New Password
                            </label>
                            <input
                              type="password"
                              value={passwordForm.newPassword}
                              onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
                              required
                              minLength={6}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Confirm New Password
                            </label>
                            <input
                              type="password"
                              value={passwordForm.confirmPassword}
                              onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
                              required
                              minLength={6}
                            />
                          </div>
                        </div>
                        <div className="mt-6">
                          <button
                            type="submit"
                            className="w-full md:w-auto px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                          >
                            Update Password
                          </button>
                        </div>
                      </form>
                    </div>

                    {/* Email Verification */}
                    {!userProfile?.isEmailVerified && (
                      <div className="bg-white rounded-2xl shadow-lg p-8">
                        <h2 className="text-2xl font-semibold mb-6">Email Verification</h2>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-gray-600">Verify your email address to secure your account</p>
                          </div>
                          <button
                            onClick={handleVerifyEmail}
                            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                          >
                            <Send className="h-4 w-4 mr-2" />
                            Send Verification Email
                          </button>
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Cancellation Modal */}
      {showCancelModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              {/* Header */}
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Cancel Booking</h3>
                  <p className="text-gray-600 mt-1">Order #{selectedOrder.id.slice(-8)}</p>
                </div>
                <button
                  onClick={() => {
                    setShowCancelModal(false);
                    setSelectedOrder(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Cancellation Policy */}
              <div className="mb-6">
                <h4 className="font-semibold text-lg mb-4">Cancellation Policy</h4>
                {(() => {
                  const policy = calculateCancellationPolicy(selectedOrder);
                  return (
                    <div className={`p-4 rounded-lg ${
                      policy.refundPercentage === 100 ? 'bg-green-50 border border-green-200' :
                      policy.refundPercentage >= 50 ? 'bg-yellow-50 border border-yellow-200' :
                      policy.refundPercentage > 0 ? 'bg-orange-50 border border-orange-200' :
                      'bg-red-50 border border-red-200'
                    }`}>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900">Refund Status:</span>
                          <span className={`font-bold ${
                            policy.refundPercentage === 100 ? 'text-green-700' :
                            policy.refundPercentage >= 50 ? 'text-yellow-700' :
                            policy.refundPercentage > 0 ? 'text-orange-700' :
                            'text-red-700'
                          }`}>
                            {policy.message}
                          </span>
                        </div>
                        
                        <div className="border-t pt-3 space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Order Total:</span>
                            <span className="font-medium">₹{selectedOrder.payment?.total?.toLocaleString() || 0}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Cancellation Fee:</span>
                            <span className="font-medium text-red-600">- ₹{policy.cancellationFee?.toLocaleString() || 0}</span>
                          </div>
                          <div className="flex justify-between text-base font-bold border-t pt-2">
                            <span>Refund Amount:</span>
                            <span className="text-green-600">₹{policy.refundAmount?.toLocaleString() || 0}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Policy Details */}
              <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h5 className="font-semibold text-blue-900 mb-2">Cancellation Fee Structure:</h5>
                <ul className="space-y-2 text-sm text-blue-800">
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span><strong>48+ hours before service:</strong> Free cancellation - 100% refund</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span><strong>24-48 hours before service:</strong> 50% cancellation fee - 50% refund</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span><strong>6-24 hours before service:</strong> 75% cancellation fee - 25% refund</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span><strong>Less than 6 hours:</strong> 100% cancellation fee - No refund</span>
                  </li>
                </ul>
              </div>

              {/* Reason for Cancellation */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Cancellation (Optional)
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  rows={4}
                  placeholder="Please let us know why you're cancelling..."
                />
              </div>

              {/* Service Details */}
              <div className="mb-6 bg-gray-50 rounded-lg p-4">
                <h5 className="font-semibold text-gray-900 mb-2">Booking Details:</h5>
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-2" />
                    <span>Service Date: {selectedOrder.scheduling?.date} at {selectedOrder.scheduling?.time}</span>
                  </div>
                  <div>
                    <span className="font-medium">Services:</span>
                    <ul className="ml-4 mt-1">
                      {selectedOrder.items?.map((item: any, index: number) => (
                        <li key={index}>• {item.name} (Qty: {item.quantity})</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-4">
                <button
                  onClick={() => {
                    setShowCancelModal(false);
                    setSelectedOrder(null);
                  }}
                  className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                >
                  Keep Booking
                </button>
                <button
                  onClick={handleConfirmCancellation}
                  disabled={cancelLoading}
                  className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {cancelLoading ? 'Processing...' : 
                   calculateCancellationFee(selectedOrder).fee > 0 
                     ? `Pay ₹${calculateCancellationFee(selectedOrder).fee} & Cancel`
                     : 'Confirm Cancellation'}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && feedbackOrder && user && (
        <PlatformFeedbackModal
          isOpen={showFeedbackModal}
          order={feedbackOrder}
          userId={user.uid}
          onClose={() => {
            setShowFeedbackModal(false);
            setFeedbackOrder(null);
          }}
          onSuccess={async () => {
            // Update the order to mark feedback as submitted
            try {
              const orderRef = doc(db, 'orders', feedbackOrder.id);
              await updateDoc(orderRef, {
                feedbackSubmitted: true,
                feedbackSubmittedAt: new Date().toISOString()
              });
              
              // Refresh orders list
              await fetchOrders();
              
              setShowFeedbackModal(false);
              setFeedbackOrder(null);
              
              setSuccessMessage('Thank you for your feedback!');
              setTimeout(() => setSuccessMessage(null), 3000);
            } catch (error) {
              console.error('Error updating order:', error);
              setError('Failed to save feedback status');
            }
          }}
        />
      )}
    </div>
  );
}
